export type QualityTier = 'high' | 'medium' | 'low' | 'off';

export interface TierProfile {
  tier: QualityTier;
  pointCount: number;
  dpr: number | [number, number];
  antialias: boolean;
  bloomEnabled: boolean;
  multisampling: number;
  frameloop: 'always' | 'demand';
  frameCap: number | null;
}

export const TIER_ORDER = ['high', 'medium', 'low', 'off'] as const satisfies readonly QualityTier[];

export const TIER_PROFILES = {
  high: {
    tier: 'high',
    pointCount: 500,
    dpr: [1, 1.5],
    antialias: true,
    bloomEnabled: true,
    multisampling: 8,
    frameloop: 'always',
    frameCap: null,
  },
  medium: {
    tier: 'medium',
    pointCount: 300,
    dpr: [1, 1.25],
    antialias: true,
    bloomEnabled: true,
    multisampling: 4,
    frameloop: 'always',
    frameCap: null,
  },
  low: {
    tier: 'low',
    pointCount: 150,
    dpr: 1,
    antialias: false,
    bloomEnabled: false,
    multisampling: 0,
    frameloop: 'demand',
    frameCap: 30,
  },
} as const satisfies Record<Exclude<QualityTier, 'off'>, TierProfile>;

// A Voronoi only seen through small reveal holes (the homepage hero) gets by on a fraction of
// the tier's budget: the grain paper and dithered edge hide the lower resolution.
const PEEK_DPR = 0.75;
const PEEK_POINT_SHARE = 0.6;

/**
 * @param profile - The resolved tier profile
 * @returns A cheaper profile for a canvas that is only glimpsed: lower dpr, fewer points, no bloom
 *   or MSAA. It keeps the tier's frame rate, so what shows through a hole moves smoothly. `null`
 *   (the `off` tier) stays `null`.
 */
export function peekProfileFor(profile: TierProfile | null): TierProfile | null {
  if (null === profile) {
    return null;
  }
  return {
    ...profile,
    pointCount: Math.round(profile.pointCount * PEEK_POINT_SHARE),
    dpr: PEEK_DPR,
    antialias: false,
    bloomEnabled: false,
    multisampling: 0,
  };
}

export const TIER_MONITOR_CONFIG = {
  sustainedWindowMs: 2000,
  minDwellMs: 4000,
  fpsMarginFactor: 0.9,
} as const;

const HIGH_TIER_MIN_SCORE = 70;
const MEDIUM_TIER_MIN_SCORE = 40;

/**
 * @param score - Capability score, 0-100 inclusive
 * @param isMobile - Whether the mobile media query matches
 * @returns The initial quality tier; never higher than `medium` on mobile
 */
export function scoreToTier(score: number, isMobile: boolean): QualityTier {
  const tier: QualityTier = HIGH_TIER_MIN_SCORE <= score ? 'high' : MEDIUM_TIER_MIN_SCORE <= score ? 'medium' : 'low';
  if (isMobile && 'high' === tier) {
    return 'medium';
  }
  return tier;
}

/**
 * @param tier - Current quality tier
 * @returns The next lower tier in TIER_ORDER; `off` has no lower tier and returns `off`
 */
export function nextTierDown(tier: QualityTier): QualityTier {
  const index = TIER_ORDER.indexOf(tier);
  if (0 > index || index >= TIER_ORDER.length - 1) {
    return 'off';
  }
  return TIER_ORDER[index + 1];
}

/**
 * @param tier - Quality tier to resolve
 * @returns The profile for a renderable tier, or null for `off`
 */
export function profileFor(tier: QualityTier): TierProfile | null {
  if ('off' === tier) {
    return null;
  }
  return TIER_PROFILES[tier];
}
